import { formCTAurl, SigninRefPath } from '@ecs/tng-common/scripts/registration';

export type IStepType = 'pl' | 'cc' | 'auto' | 'fcr' | 'fcs';

export interface IStep {
  title: string;
  content: string;
  imgSrc: string;
  imgAlt: string;
}
export type IStepIdValue =
  | 'stepPL'
  | 'stepCC'
  | 'stepCC-balancetransfer'
  | 'stepCC-rewards'
  | 'stepCC-buildcredit'
  | 'stepCC-lowinterest'
  | 'stepCC-travel'
  | 'stepCC-noannualfee'
  | 'stepAuto-ins'
  | 'stepAuto-garage'
  | 'stepFCR'
  | 'stepFCS';

export type ISingleSteps = {
  type: IStepType;
  id: IStepIdValue;
  ctaUrl: string;
  title: string;
  steps: IStep[];
};
export type TypeCommonContent = {
  ctaTextCRM: string;
  ctaText: string;
  assetPrefix: string;
};
export const typeCommonContent: Record<IStepType, TypeCommonContent> = {
  pl: {
    ctaText: 'See if you qualify',
    ctaTextCRM: 'Sign in',
    assetPrefix: '/graphics/blog-step-graphics/personal-loan'
  },
  cc: {
    ctaText: 'See your offers',
    ctaTextCRM: 'Sign in',
    assetPrefix: '/graphics/blog-step-graphics/credit-cards'
  },
  auto: {
    ctaText: 'Get started',
    ctaTextCRM: 'Sign in',
    assetPrefix: '/graphics/blog-step-graphics/auto'
  },
  fcr: {
    ctaText: 'Get your free credit report',
    ctaTextCRM: 'Sign in',
    assetPrefix: '/graphics/blog-step-graphics'
  },
  fcs: {
    ctaText: 'Get your FICO<sup>®</sup> Score',
    ctaTextCRM: 'Sign in',
    assetPrefix: '/graphics/blog-step-graphics'
  }
};

// IMG ALT values ARE INCORRECT
export const stepGraphicsContent: Record<IStepIdValue, ISingleSteps> = {
  stepPL: {
    type: 'pl',
    id: 'stepPL',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.personalLoans,
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Find a personal loan matched for you',
    steps: [
      {
        title: 'Pick your loan type',
        content: 'Let us know what type of loan you’re looking for from a list of options.',
        imgSrc: '/img-stepbystep-blog-loan-1-dt.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Fill out your info',
        content: 'Tell us your income and address—then verify everything is correct.',
        imgSrc: '/img-stepbystep-blog-loan-2-dt.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Browse your offers',
        content: 'See and compare your best loan offers with no impact to your credit.',
        imgSrc: '/img-stepbystep-blog-loan-3-dt.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  stepCC: {
    type: 'cc',
    id: 'stepCC',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards,
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Compare top credit cards matched for you',
    steps: [
      {
        title: 'Sign up for free',
        content: 'See your current credit card offers based on your credit profile.',
        imgSrc: '/graphic-get-started-credit-profile-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Choose the card for you',
        content: 'From cash back rewards to a low intro APR, compare your options.',
        imgSrc: '/graphic-choose-card-cash-back-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Apply with confidence',
        content: 'Apply knowing you’re more likely to qualify with matched offers.',
        imgSrc: '/graphic-apply-for-credit-card-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepCC-balancetransfer': {
    type: 'cc',
    id: 'stepCC-balancetransfer',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards + '?features=balancetransferallowed',
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Save with an intro 0% APR balance transfer',
    steps: [
      {
        title: 'Sign up for free',
        content: 'See the best credit card offers you’re more likely to qualify for.',
        imgSrc: '/graphic-get-started-credit-profile-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Choose your balance transfer card',
        content:
          'Pick from top balance transfer cards with lengthy intro 0% APR periods on balance transfers.',
        imgSrc: '/graphic-choose-card-balance-transfer-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Apply with confidence',
        content: 'Apply and pay off high-interest credit card debt at a lower interest rate.',
        imgSrc: '/graphic-apply-for-credit-card-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepCC-rewards': {
    type: 'cc',
    id: 'stepCC-rewards',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards,
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Earn rewards for your spending',
    steps: [
      {
        title: 'Sign up for free',
        content:
          'See your top credit card matches based on your credit profile and spending habits.',
        imgSrc: '/graphic-get-started-credit-profile-screen.webp',
        imgAlt: 'Graphic get started credit profile screen.'
      },
      {
        title: 'Choose your rewards card',
        content:
          'Get the right card to earn cash back or points on your purchases and maximize your perks.',
        imgSrc: '/graphic-choose-card-cash-back-screen.webp',
        imgAlt: 'Graphic choose card cash back screen.'
      },
      {
        title: 'Apply with confidence',
        content:
          'Apply for rewards cards matched for you and that you’re more likely to qualify for.',
        imgSrc: '/graphic-apply-for-credit-card-screen.webp',
        imgAlt: 'Graphic apply for credit card screen.'
      }
    ]
  },
  'stepCC-buildcredit': {
    type: 'cc',
    id: 'stepCC-buildcredit',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards,
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Start building your credit',
    steps: [
      {
        title: 'Sign up for free',
        content:
          'Whether you are new to credit or rebuilding your credit, consider these starter cards.',
        imgSrc: '/graphic-get-started-welcome-check-credit-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Get the right card for you',
        content: 'See and compare offers, including some cards with rewards and low fees.',
        imgSrc: '/graphic-choose-card-cash-back-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Improve your credit',
        content:
          'Use your credit card, pay your bill on time and monitor your credit score progress for free.',
        imgSrc: '/graphic-monitor-spending-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepCC-lowinterest': {
    type: 'cc',
    id: 'stepCC-lowinterest',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards + '?features=lowinterestrate',
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Save with 0% intro APR credit cards',
    steps: [
      {
        title: 'Sign up for free',
        content: 'Get personalized offers you’re more likely to qualify for.',
        imgSrc: '/graphic-get-started-credit-profile-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Choose your credit card',
        content: 'Make interest-free payments for a period of time on your new purchases.',
        imgSrc: '/graphic-choose-card-apr-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Apply and start saving',
        content: 'Apply with confidence and start saving money.',
        imgSrc: '/graphic-apply-for-credit-card-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepCC-travel': {
    type: 'cc',
    id: 'stepCC-travel',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards + '?features=travel',
      sem: '1',
      showDisclaimer: true
    }),

    title: 'Earn rewards for hotel stays and flights',
    steps: [
      {
        title: 'Sign up for free',
        content: 'See personalized offers based on your credit profile.',
        imgSrc: '/graphic-get-started-credit-profile-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Get the best travel card for you',
        content: 'Compare cards that offer intro bonuses, rewards and travel perks.',
        imgSrc: '/graphic-choose-card-travel-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Start planning your next trip',
        content: 'Earn rewards to help offset your next vacation.',
        imgSrc: '/graphic-apply-for-credit-card-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepCC-noannualfee': {
    type: 'cc',
    id: 'stepCC-noannualfee',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.creditCards + '?features=noannualfee',
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Save with no annual fee credit cards',
    steps: [
      {
        title: 'Sign up for free',
        content: 'See the best credit card offers based on your credit profile.',
        imgSrc: '/graphic-get-started-credit-profile-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Choose your no annual fee card',
        content:
          'See and compare no annual fee offers that can earn rewards or offer intro 0% APRs.',
        imgSrc: '/graphic-choose-card-no-annual-fee-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Apply with confidence',
        content: 'Apply for no annual fee cards that you’re more likely to qualify for.',
        imgSrc: '/graphic-apply-for-credit-card-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepAuto-ins': {
    type: 'auto',
    id: 'stepAuto-ins',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.insuranceHub,
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Stop overpaying for insurance',
    steps: [
      {
        title: 'Match with new policies',
        content:
          'Answer some questions about your current plan and get matched to personalized quotes.',
        imgSrc: '/insurance/graphic-blog-get-started-whats-your-insurance-company.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Pick the one you want',
        content:
          'We’ll show you offers from our top insurance carriers and help you shop for better rates in the future.',
        imgSrc: '/insurance/graphic-blog-top-insurance-quotes.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Let us do the rest',
        content:
          'From switching your policy to canceling and requesting a partial refund on your old plan, we help you do it all.',
        imgSrc: '/insurance/graphic-blog-information-recieved-congrats.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  'stepAuto-garage': {
    type: 'auto',
    id: 'stepAuto-garage',
    ctaUrl: formCTAurl({
      offer: 'at_frsas109',
      authType: '102',
      refUrl: SigninRefPath.carGarage,
      sem: '1',
      showDisclaimer: true
    }),
    title: 'Manage all of your vehicles in one place',
    steps: [
      {
        title: 'Sign up for a free account',
        content: 'Make vehicle ownership, maintenance and management easy with our free account.',
        imgSrc: '/garage/graphic-blog-add-your-vehicle.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Add your vehicle information',
        content: 'Enter information about your vehicles and we’ll find you potential auto savings.',
        imgSrc: '/garage/graphic-blog-enter-vehicle-details.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Stay on top of maintenance',
        content:
          'Monitor your vehicle history reports, open recalls, car value history and market trends.',
        imgSrc: '/garage/graphic-blog-your-vehicle-detail-alert.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  stepFCR: {
    type: 'fcr',
    id: 'stepFCR',
    ctaUrl: formCTAurl({
      offer: 'at_frsas119',
      authType: '102',
      dAuth: true
    }),
    title: 'See your latest credit information',
    steps: [
      {
        title: 'Stay on top of your credit',
        content: 'Better understand your creditworthiness with an overview of your total debt.',
        imgSrc: '/graphic-experian-report-open-accounts-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'Get free credit monitoring',
        content: 'Check for any changes with customized alerts and an updated report daily.',
        imgSrc: '/graphic-notifications-alerts-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Protect yourself from fraud',
        content:
          'Reviewing your credit report can help you spot potential fraud or identity theft.',
        imgSrc: '/graphic-credit-usage-and-card-balance-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  },
  stepFCS: {
    type: 'fcs',
    id: 'stepFCS',
    ctaUrl: formCTAurl({
      offer: 'at_frsas119',
      authType: '102',
      showDisclaimer: true,
      dAuth: true
    }),
    title: 'Get your FICO<sup>®</sup> Score<sup>*</sup> for free',
    steps: [
      {
        title: 'No credit card required',
        content: 'Sign up and get instant online access to your FICO<sup>®</sup> Score for free.',
        imgSrc: '/graphic-score-account-screen.webp',
        imgAlt: 'Step 1'
      },
      {
        title: 'No impact to your score',
        content: 'Checking your own credit won’t lower your credit scores.',
        imgSrc: '/graphic-score-factors-and-ingredients-screen.webp',
        imgAlt: 'Step 2'
      },
      {
        title: 'Understand your FICO<sup>®</sup> Score',
        content: 'View specific factors that are affecting your score and how to improve it.',
        imgSrc: '/graphic-experian-report-open-accounts-screen.webp',
        imgAlt: 'Step 3'
      }
    ]
  }
};
